import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Search, RefreshCw, X, Building2, Globe, MapPin, Users, DollarSign,
  ExternalLink, CheckCircle2, FileDown, ShieldCheck, ShieldAlert, Shield,
  AlertTriangle, Loader2, Cpu, Briefcase, TrendingUp,
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import VerificationBadge from "../../components/VerificationBadge";
import IntelligenceModal from "../../components/IntelligenceModal";
import CompanyEventsTimeline from "../../components/CompanyEventsTimeline";
import SideFilters from "../../components/SideFilters";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SECTOR_PALETTE = ["#0891B2", "#7C3AED", "#DB2777", "#059669", "#B45309", "#0369A1"];
function accentForSector(s = "") {
  let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return SECTOR_PALETTE[Math.abs(h) % SECTOR_PALETTE.length];
}

// ─── Company Row ──────────────────────────────────────────────────────────
function getSizeLabel(size) {
  if (size >= 500) return "ENTERPRISE";
  if (size >= 50)  return "MID-MARKET";
  if (size > 0)    return "SMB";
  return null;
}

function CompanyRow({ c, onOpen }) {
  const accent = accentForSector(c.sector || "");
  const size = c.employees || 0;
  const sizeLabel = getSizeLabel(size);
  const goSimulate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = `/entity-loss-simulation?name=${encodeURIComponent(c.name)}`;
  };
  const goCyberScan = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = `/cyber-exposure?q=${encodeURIComponent(c.name)}`;
  };
  return (
    <button
      data-testid={`business-row-${c.id}`}
      onClick={() => onOpen(c)}
      className="w-full group text-left bg-[#0D0F12] border border-white/[0.04] hover:border-[#00E5FF]/60 transition-all"
    >
      <div className="h-0.5 opacity-70" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
      <div className="p-4 flex items-center gap-4">
        <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center border" style={{ background: `${accent}18`, borderColor: `${accent}55` }}>
          <Building2 className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{c.name}</span>
            <VerificationBadge status={c.verification_status} size="xs" profileId={c.id} />
            {sizeLabel && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 border border-white/[0.06] text-[#A0AEC0]">{sizeLabel}</span>}
          </div>
          <p className="text-xs text-[#CBD5E1] truncate">
            {c.sector || "—"}
            {c.hq && <span className="text-[#A0AEC0]"> · {c.hq}</span>}
          </p>
          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#6B7280]">
            {c.employees > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{c.employees.toLocaleString("it-IT")} dip.</span>}
            {c.revenue && <span className="flex items-center gap-1 text-[#16C784]"><DollarSign className="w-3 h-3" />{c.revenue}</span>}
            {c.domain && <span className="flex items-center gap-1 text-[#00E5FF] truncate max-w-[200px]"><Globe className="w-3 h-3" />{c.domain}</span>}
          </div>
        </div>
        <span
          role="link"
          tabIndex={0}
          onClick={goCyberScan}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goCyberScan(e)}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-[9px] tracking-widest uppercase font-bold text-[#00E5FF] hover:text-white border border-[#00E5FF]/40 hover:border-white px-2 py-1 cursor-pointer"
          style={{ borderRadius: "2px" }}
          title="Cyber scan"
          data-testid={`business-row-cyberscan-${c.id}`}
        >
          Cyber scan
        </span>
        <span
          role="link"
          tabIndex={0}
          onClick={goSimulate}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goSimulate(e)}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-[9px] tracking-widest uppercase font-bold text-[#FF1744] hover:text-white border border-[#FF1744]/40 hover:border-white px-2 py-1 cursor-pointer"
          style={{ borderRadius: "2px" }}
          title="Simula perdita entità"
          data-testid={`business-row-simulate-${c.id}`}
        >
          Simula perdita
        </span>
        <ExternalLink className="w-4 h-4 text-[#6B7280] group-hover:text-[#00E5FF] transition-colors flex-shrink-0" />
      </div>
    </button>
  );
}

// ─── Deterministic Financial Analysis (demo) ─────────────────────────────
function _hash32(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h >>> 0;
}
function _rng(seed) {
  // xorshift32
  let s = seed || 1;
  return () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return ((s >>> 0) / 0xFFFFFFFF); };
}
function financialsFor(business) {
  const key = business?.id || business?.name || "x";
  const seed = _hash32(String(key));
  const rand = _rng(seed);
  // Base revenue from Apollo revenue string if available
  let baseRev = 8_000_000 + Math.floor(rand() * 120_000_000);
  const revStr = String(business?.revenue || "");
  const m = revStr.match(/(\d[\d.,]*)\s*([KMB])?/i);
  if (m) {
    let v = parseFloat(m[1].replace(/\./g, "").replace(",", "."));
    const u = (m[2] || "").toUpperCase();
    if (u === "K") v *= 1e3; else if (u === "M") v *= 1e6; else if (u === "B") v *= 1e9;
    if (!isNaN(v) && v > 0) baseRev = Math.floor(v);
  }
  const growth1 = 0.85 + rand() * 0.30;   // -15% .. +15%
  const growth2 = 0.85 + rand() * 0.30;
  const rev = [Math.floor(baseRev * growth1 * growth2), Math.floor(baseRev * growth1), baseRev];
  const ebitdaPct = 0.06 + rand() * 0.18;  // 6-24%
  const ebitda = rev.map(r => Math.floor(r * (ebitdaPct + (rand() - 0.5) * 0.03)));
  const pfnRatio = -0.4 + rand() * 2.0;    // -0.4x .. +1.6x EBITDA
  const pfn = ebitda.map(e => Math.floor(e * pfnRatio));
  const equity = Math.floor(baseRev * (0.15 + rand() * 0.35));
  const netIncome = Math.floor(ebitda[2] * (0.4 + rand() * 0.35));
  const roe = equity > 0 ? (netIncome / equity) * 100 : 0;
  const currentRatio = 1.0 + rand() * 1.8;
  const dToE = pfnRatio > 0 ? Math.max(0.2, pfnRatio * 1.4) : 0.3 + rand() * 0.6;

  // Credit score 0-100 based on growth + margins + leverage
  const growthScore  = Math.min(30, Math.max(0, ((growth2 - 1) * 100 + 5) * 3));
  const marginScore  = Math.min(30, ebitdaPct * 120);
  const leverageScore = Math.min(25, Math.max(0, 25 - Math.max(0, pfnRatio) * 12));
  const equityScore  = Math.min(15, (equity / baseRev) * 40);
  const score = Math.round(growthScore + marginScore + leverageScore + equityScore);
  const scoreClamped = Math.max(15, Math.min(96, score));

  // Suggested credit line: ~ 5-15% of latest revenue weighted by score
  const factor = 0.02 + (scoreClamped / 100) * 0.13;
  let credit = Math.round(rev[2] * factor / 5000) * 5000;
  credit = Math.max(20_000, Math.min(credit, 5_000_000));

  const rating = scoreClamped >= 75 ? { label: "AA · Basso rischio", color: "#00E676" }
              : scoreClamped >= 55 ? { label: "BBB · Rischio medio", color: "#FFB800" }
              : { label: "CCC · Rischio elevato", color: "#FF1744" };

  const years = [new Date().getFullYear() - 2, new Date().getFullYear() - 1, new Date().getFullYear()];
  return { years, rev, ebitda, pfn, roe, currentRatio, dToE, score: scoreClamped, credit, rating };
}
function fmtEUR(v, compact = true) {
  const n = Math.round(v);
  if (compact) {
    if (Math.abs(n) >= 1e9) return `${(n/1e9).toFixed(1).replace(".",",")} Mld €`;
    if (Math.abs(n) >= 1e6) return `${(n/1e6).toFixed(1).replace(".",",")} M €`;
    if (Math.abs(n) >= 1e3) return `${(n/1e3).toFixed(0)} K €`;
    return `${n} €`;
  }
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

// ─── Financial Analysis Panel ────────────────────────────────────────────
function FinancialsPanel({ business }) {
  const f = React.useMemo(() => financialsFor(business), [business]);
  const trend = f.rev[2] > f.rev[0]
    ? { icon: "↗", color: "#00E676", label: "In crescita" }
    : { icon: "↘", color: "#FF1744", label: "In contrazione" };
  return (
    <div data-testid="financial-analysis-panel" className="bg-[#0A0C10] border border-white/[0.04] p-4" style={{ borderRadius: "2px" }}>
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-4 h-4 text-[#00E5FF]" />
        <p className="text-xs tracking-widest uppercase text-[#00E5FF] font-bold">Analisi Finanziaria &amp; Credito</p>
        <span className="ml-auto text-[9px] tracking-widest uppercase text-[#6B7280]">Dati proceduralmente derivati · Demo</span>
      </div>

      {/* Credit Score + Fido + Rating */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {/* Credit Score */}
        <div data-testid="credit-score-card" className="bg-white p-4" style={{ borderRadius: "2px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <p className="text-[9px] tracking-[0.22em] uppercase text-slate-500 font-semibold mb-1">Credit Score</p>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: "Chivo, sans-serif", color: "#0D1B3D" }}>{f.score}</span>
            <span className="text-sm font-semibold" style={{ color: "#0D1B3D", opacity: 0.55 }}>/100</span>
          </div>
          <div className="mt-2 h-1.5 bg-slate-200 overflow-hidden" style={{ borderRadius: "2px" }}>
            <div style={{ width: `${f.score}%`, height: "100%", backgroundColor: f.rating.color, transition: "width 300ms" }} />
          </div>
        </div>

        {/* Fido consigliato */}
        <div data-testid="credit-line-card" className="bg-white p-4" style={{ borderRadius: "2px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <p className="text-[9px] tracking-[0.22em] uppercase text-slate-500 font-semibold mb-1">Fido commerciale consigliato</p>
          <p className="text-2xl font-bold tabular-nums" style={{ fontFamily: "Chivo, sans-serif", color: "#0D1B3D" }}>{fmtEUR(f.credit)}</p>
          <p className="text-[10px] text-slate-500 mt-1">Esposizione mensile suggerita</p>
        </div>

        {/* Rating */}
        <div data-testid="risk-rating-card" className="bg-white p-4" style={{ borderRadius: "2px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <p className="text-[9px] tracking-[0.22em] uppercase text-slate-500 font-semibold mb-1">Rating rischio</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: f.rating.color, boxShadow: `0 0 8px ${f.rating.color}` }} />
            <span className="text-sm font-bold tabular-nums" style={{ fontFamily: "Chivo, sans-serif", color: "#0D1B3D" }}>{f.rating.label}</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">
            Trend: <span style={{ color: trend.color, fontWeight: 700 }}>{trend.icon} {trend.label}</span>
          </p>
        </div>
      </div>

      {/* Bilancio 3 anni */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="text-left py-2 px-2 text-[9px] tracking-[0.2em] uppercase text-[#6B7280] font-semibold">Voce</th>
              {f.years.map(y => (
                <th key={y} className="text-right py-2 px-2 text-[9px] tracking-[0.2em] uppercase text-[#6B7280] font-semibold tabular-nums">{y}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-white">
            <tr className="border-b border-white/[0.03]">
              <td className="py-2 px-2 text-[#CBD5E1]">Ricavi</td>
              {f.rev.map((v, i) => (
                <td key={i} className="text-right py-2 px-2 tabular-nums font-semibold">{fmtEUR(v)}</td>
              ))}
            </tr>
            <tr className="border-b border-white/[0.03]">
              <td className="py-2 px-2 text-[#CBD5E1]">EBITDA</td>
              {f.ebitda.map((v, i) => (
                <td key={i} className="text-right py-2 px-2 tabular-nums font-semibold" style={{ color: v >= 0 ? "#00E676" : "#FF1744" }}>{fmtEUR(v)}</td>
              ))}
            </tr>
            <tr className="border-b border-white/[0.03]">
              <td className="py-2 px-2 text-[#CBD5E1]">Posizione Finanziaria Netta</td>
              {f.pfn.map((v, i) => (
                <td key={i} className="text-right py-2 px-2 tabular-nums font-semibold" style={{ color: v > 0 ? "#FFB800" : "#00E676" }}>{fmtEUR(v)}</td>
              ))}
            </tr>
            <tr>
              <td className="py-2 px-2 text-[#CBD5E1]">ROE</td>
              <td className="text-right py-2 px-2 tabular-nums text-[#6B7280]">—</td>
              <td className="text-right py-2 px-2 tabular-nums text-[#6B7280]">—</td>
              <td className="text-right py-2 px-2 tabular-nums font-semibold" style={{ color: f.roe >= 8 ? "#00E676" : f.roe >= 3 ? "#FFB800" : "#FF1744" }}>{f.roe.toFixed(1).replace(".",",")}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Ratios */}
      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/[0.06]">
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-[#6B7280] tracking-widest uppercase">Current Ratio</span>
          <span className="text-white font-semibold tabular-nums">{f.currentRatio.toFixed(2).replace(".",",")}</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-[#6B7280] tracking-widest uppercase">Debt / Equity</span>
          <span className="text-white font-semibold tabular-nums">{f.dToE.toFixed(2).replace(".",",")}</span>
        </div>
      </div>
    </div>
  );
}

// ─── KYC Compliance Sub-Panel ─────────────────────────────────────────────
function KycPanel({ businessId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/kyc/company/${businessId}/compliance-check`, { withCredentials: true })
      .then(r => { if (mounted) { setData(r.data); setError(""); } })
      .catch(e => { if (mounted) setError(e.response?.data?.detail || "Errore KYC"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [businessId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#A0AEC0]">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00E5FF]" /> Verifica KYC in corso…
      </div>
    );
  }
  if (error || !data) {
    return <p className="text-xs text-[#FF3B30]">{error || "Errore KYC"}</p>;
  }

  const verdictMeta = {
    compliant:      { color: "#059669", icon: ShieldCheck, label: "COMPLIANT", desc: "Nessuna criticità rilevata" },
    manual_review:  { color: "#B45309", icon: Shield,      label: "MANUAL REVIEW", desc: "Attenzione richiesta — check manuale consigliato" },
    fraud:          { color: "#DC2626", icon: ShieldAlert, label: "NON COMPLIANT", desc: "Match critico su liste PEP/sanction" },
  };
  const v = verdictMeta[data.verdict] || verdictMeta.manual_review;
  const VIcon = v.icon;

  return (
    <div className="space-y-3">
      {/* Verdict badge */}
      <div className="flex items-start gap-3 p-3 border" style={{ borderColor: `${v.color}55`, background: `${v.color}10` }}>
        <VIcon className="w-6 h-6 flex-shrink-0" style={{ color: v.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-bold tracking-widest uppercase" style={{ color: v.color, fontFamily: "Chivo, sans-serif" }}>{v.label}</span>
            <span className="text-[10px] tracking-widest uppercase text-[#A0AEC0]">Risk score:</span>
            <span className="text-sm font-bold tabular-nums" style={{ color: v.color }}>{data.risk_score}/100</span>
          </div>
          <p className="text-xs text-[#CBD5E1] mt-0.5">{v.desc}</p>
          <p className="text-[10px] text-[#6B7280] mt-1">Ultimo check: {new Date(data.last_checked).toLocaleString("it-IT")} · {data.source}</p>
        </div>
      </div>

      {/* Checks list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
        {data.checks.map((chk, i) => {
          const STATUS_COLOR = { pass: "#059669", warn: "#B45309", fail: "#DC2626" };
          const stColor = STATUS_COLOR[chk.status] || "#DC2626";
          return (
            <div key={chk.name || i} data-testid={`kyc-check-${i}`} className="flex items-start gap-2 px-3 py-2 border border-white/[0.04]">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: stColor, boxShadow: `0 0 6px ${stColor}` }} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-white font-semibold">{chk.name}</p>
                <p className="text-[10px] text-[#A0AEC0] leading-relaxed">{chk.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Helpers: employee/revenue range bucketing
const EMP_BUCKETS = [
  { value: "1-10",    label: "1–10",         test: (n) => n >= 1 && n <= 10 },
  { value: "11-50",   label: "11–50",        test: (n) => n >= 11 && n <= 50 },
  { value: "51-200",  label: "51–200",       test: (n) => n >= 51 && n <= 200 },
  { value: "201-500", label: "201–500",      test: (n) => n >= 201 && n <= 500 },
  { value: "501-1000",label: "501–1000",     test: (n) => n >= 501 && n <= 1000 },
  { value: "1001+",   label: "1.001+",       test: (n) => n > 1000 },
];

function parseRevenue(revStr) {
  if (!revStr || typeof revStr !== "string") return 0;
  const cleaned = revStr.replace(/[€$\s]/g, "");
  const m = cleaned.match(/([\d.,]+)\s*([KMB])?/i);
  if (!m) return 0;
  const num = parseFloat(m[1].replace(",", "."));
  const mult = { K: 1e3, M: 1e6, B: 1e9 }[(m[2] || "").toUpperCase()] || 1;
  return num * mult;
}

const REV_BUCKETS = [
  { value: "0-1M",     label: "< 1M €",     test: (r) => r < 1e6 },
  { value: "1M-10M",   label: "1M – 10M",    test: (r) => r >= 1e6 && r < 10e6 },
  { value: "10M-50M",  label: "10M – 50M",   test: (r) => r >= 10e6 && r < 50e6 },
  { value: "50M-250M", label: "50M – 250M",  test: (r) => r >= 50e6 && r < 250e6 },
  { value: "250M-1B",  label: "250M – 1B",   test: (r) => r >= 250e6 && r < 1e9 },
  { value: "1B+",      label: "1B+",         test: (r) => r >= 1e9 },
];

export default function BusinessList() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openBiz, setOpenBiz] = useState(null);
  const [downloadingBilancio, setDownloadingBilancio] = useState(false);
  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sales/businesses`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply text search + massive facet filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fSectors     = filters.sector     || [];
    const fEmpBuckets  = filters.employees  || [];
    const fRevBuckets  = filters.revenue    || [];
    const fVerif       = filters.verification || [];
    const fLocation    = (filters.location || "").trim().toLowerCase();
    const fEnterprise  = filters.enterprise === true;
    const fWebsite     = filters.has_website === true;
    const fTech        = filters.has_tech === true;

    return items.filter(c => {
      if (q && ![c.name, c.sector, c.hq, c.domain].some(v => (v || "").toLowerCase().includes(q))) return false;
      if (fSectors.length && !fSectors.includes(c.sector)) return false;
      if (fEmpBuckets.length && !fEmpBuckets.some(b => EMP_BUCKETS.find(x => x.value === b)?.test(c.employees || 0))) return false;
      if (fRevBuckets.length && !fRevBuckets.some(b => REV_BUCKETS.find(x => x.value === b)?.test(parseRevenue(c.revenue)))) return false;
      if (fVerif.length && !fVerif.includes((c.verification_status || "unknown").toLowerCase())) return false;
      if (fLocation && !(c.hq || "").toLowerCase().includes(fLocation)) return false;
      if (fEnterprise && (c.employees || 0) < 500) return false;
      if (fWebsite && !c.domain) return false;
      if (fTech && !(c.technologies && c.technologies.length > 0)) return false;
      return true;
    });
  }, [items, search, filters]);

  // Facet options + counts derived from data
  const facetSections = useMemo(() => {
    const sectorMap = new Map();
    items.forEach(c => {
      if (!c.sector) return;
      sectorMap.set(c.sector, (sectorMap.get(c.sector) || 0) + 1);
    });
    const sectorOpts = Array.from(sectorMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([s, n]) => ({ value: s, label: s, count: n }));

    const empOpts = EMP_BUCKETS.map(b => ({
      value: b.value, label: b.label,
      count: items.filter(c => b.test(c.employees || 0)).length,
    })).filter(o => o.count > 0);

    const revOpts = REV_BUCKETS.map(b => ({
      value: b.value, label: b.label,
      count: items.filter(c => b.test(parseRevenue(c.revenue))).length,
    })).filter(o => o.count > 0);

    const verifOpts = ["verified", "unknown", "suspicious", "fraud"].map(v => ({
      value: v, label: v.charAt(0).toUpperCase() + v.slice(1),
      count: items.filter(c => (c.verification_status || "unknown").toLowerCase() === v).length,
    })).filter(o => o.count > 0);

    return [
      { id: "sector",       label: "Settore",       icon: Briefcase, type: "checkbox", options: sectorOpts },
      { id: "employees",    label: "Dimensione",    icon: Users,     type: "checkbox", options: empOpts },
      { id: "revenue",      label: "Fatturato",     icon: DollarSign,type: "checkbox", options: revOpts },
      { id: "verification", label: "Verifica",      icon: ShieldCheck,type: "checkbox", options: verifOpts },
      { id: "location",     label: "Sede",          icon: MapPin,    type: "text",     placeholder: "es. Milano, Berlin…" },
      { id: "enterprise",   label: "Enterprise 500+", icon: TrendingUp, type: "toggle", toggleLabel: "Solo 500+ dip." },
      { id: "has_website",  label: "Sito web",      icon: Globe,     type: "toggle",   toggleLabel: "Con sito web" },
      { id: "has_tech",     label: "Tech stack",    icon: Cpu,       type: "toggle",   toggleLabel: "Con tech stack" },
    ];
  }, [items]);

  const kpis = useMemo(() => {
    const verified = items.filter(c => (c.verification_status || "").toLowerCase() === "verified").length;
    const sectors = new Set(items.map(c => c.sector).filter(Boolean)).size;
    const enterprise = items.filter(c => (c.employees || 0) >= 500).length;
    return { total: items.length, verified, sectors, enterprise };
  }, [items]);

  const downloadBilancio = async (biz) => {
    setDownloadingBilancio(true);
    try {
      const r = await axios.get(`${API}/sales/businesses/${biz.id}/financial-statement.pdf`, {
        withCredentials: true, responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `bilancio_${(biz.name || "azienda").replace(/[^a-z0-9]/gi, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert("Bilancio non disponibile per questa azienda");
    } finally { setDownloadingBilancio(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 min-h-full">
      {/* Header (full-width) */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-2 text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] font-bold">
            <Building2 className="w-3 h-3" /> Intelligence · Business List
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Business List</h1>
          <p className="text-sm mt-1 max-w-2xl" style={{ color: "#475569" }}>
            Tutte le aziende salvate dal tuo tenant. Click su una riga per aprire la scheda completa con OSINT · SOCMINT · HUMINT, download bilancio e KYC compliance check.
          </p>
        </div>
        <button data-testid="refresh-business-list" onClick={load} className="flex items-center gap-1.5 bg-[#1F242D] text-white border border-white/[0.06] px-3 py-2 text-xs hover:bg-[#30363D] transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </button>
      </div>

      {/* KPI bar (full-width) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Totale aziende", value: kpis.total, color: "#0891B2", icon: Building2 },
          { label: "Verificate", value: kpis.verified, color: "#059669", icon: CheckCircle2 },
          { label: "Enterprise 500+", value: kpis.enterprise, color: "#DB2777", icon: Users },
          { label: "Settori coperti", value: kpis.sectors, color: "#7C3AED", icon: Cpu },
        ].map(kpi => (
          <div key={kpi.label} className="border border-white/[0.04] bg-[#0D0F12] p-4 flex items-center justify-between">
            <div>
              <p className="text-[9px] tracking-[0.2em] uppercase text-[#A0AEC0] font-semibold mb-1">{kpi.label}</p>
              <p className="text-2xl font-bold tabular-nums" style={{ color: kpi.color, fontFamily: "Chivo, sans-serif" }}>{kpi.value}</p>
            </div>
            <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
          </div>
        ))}
      </div>

      {/* Body: sidebar filtri + main (search + lista) */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Sidebar filtri laterali (altezza pari al search bar) */}
        <SideFilters
          open={filtersOpen} setOpen={setFiltersOpen}
          filters={filters} setFilters={setFilters}
          sections={facetSections}
          totalMatch={filtered.length} totalAll={items.length}
          entity="business"
        />

        {/* Main: search + lista */}
        <div className="flex-1 min-w-0 space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
        <input
          data-testid="business-list-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filtra per ragione sociale, settore, sede, dominio…"
          className="w-full bg-[#050505] border border-white/[0.04] text-white pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#6B7280]"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7280] hover:text-white transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-16 tracking-widest uppercase text-xs" style={{ color: "#64748B" }}>Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/[0.04] bg-[#0D0F12] py-16 text-center">
          <Building2 className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-white font-semibold">Nessuna azienda salvata</p>
          <p className="text-xs text-[#A0AEC0] mt-1">Vai su <span className="text-[#00E5FF]">Company Check</span> per cercare e aggiungere aziende al tuo database.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => <CompanyRow key={c.id} c={c} onOpen={setOpenBiz} />)}
        </div>
      )}

      {/* Detail modal */}
      {openBiz && (
        <IntelligenceModal
          targetId={openBiz.id}
          targetType="company"
          title={openBiz.name}
          subtitle={[openBiz.sector, openBiz.hq].filter(Boolean).join(" · ")}
          onClose={() => setOpenBiz(null)}
        >
          {/* Business identity + KPI + actions */}
          <div className="space-y-5">
            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                ["Dipendenti", openBiz.employees ? openBiz.employees.toLocaleString("it-IT") : "—", Users, "#0891B2"],
                ["Fatturato", openBiz.revenue || "—", DollarSign, "#059669"],
                ["Sede", openBiz.hq || "—", MapPin, "#B45309"],
                ["Fondata", openBiz.founded_year || "—", Building2, "#7C3AED"],
              ].map(([label, value, Icon, color]) => (
                <div key={label} className="border border-white/[0.04] p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className="w-3 h-3" style={{ color }} />
                    <p className="text-[9px] tracking-widest uppercase text-[#A0AEC0]">{label}</p>
                  </div>
                  <p className="text-sm font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }} title={String(value)}>{value}</p>
                </div>
              ))}
            </div>

            {/* Website + Tech chips */}
            {(openBiz.domain || (openBiz.technologies && openBiz.technologies.length > 0)) && (
              <div className="space-y-2">
                {openBiz.domain && (
                  <a href={openBiz.domain.startsWith("http") ? openBiz.domain : `https://${openBiz.domain}`} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1.5 text-xs text-[#00E5FF] hover:text-white transition-colors">
                    <Globe className="w-3 h-3" /> {openBiz.domain} <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                  </a>
                )}
                {openBiz.technologies && openBiz.technologies.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {openBiz.technologies.slice(0, 8).map(t => (
                      <span key={t} className="px-1.5 py-0.5 bg-[#050505] border border-white/[0.04] text-[9px] text-[#CBD5E1] tracking-wide">{t}</span>
                    ))}
                    {openBiz.technologies.length > 8 && (
                      <span className="text-[9px] text-[#6B7280] px-1 self-center">+{openBiz.technologies.length - 8}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action bar: download bilancio */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                data-testid="download-bilancio-btn"
                onClick={() => downloadBilancio(openBiz)}
                disabled={downloadingBilancio}
                className="inline-flex items-center gap-2 bg-[#16C784] text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-60"
              >
                {downloadingBilancio ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                {downloadingBilancio ? "Genero PDF…" : "Scarica bilancio"}
              </button>
              <span className="text-[10px] tracking-widest uppercase text-[#6B7280]">PDF · dati procedurali demo</span>
            </div>

            {/* Analisi Finanziaria & Credito */}
            <FinancialsPanel business={openBiz} />

            {/* KYC compliance */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-[#00E5FF]" />
                <p className="text-xs tracking-widest uppercase text-[#00E5FF] font-bold">KYC Compliance Check</p>
              </div>
              <KycPanel businessId={openBiz.id} />
            </div>

            {/* Timeline B2B Events */}
            <div className="bg-[#0A0C10] border border-white/[0.04] p-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-[#A78BFA]" />
                <p className="text-xs tracking-widest uppercase text-[#A78BFA] font-bold">Timeline B2B Events</p>
              </div>
              <CompanyEventsTimeline companyName={openBiz.name} />
            </div>
          </div>
        </IntelligenceModal>
      )}
        </div>{/* /main search+lista */}
      </div>{/* /body flex sidebar+main */}
    </div>
  );
}
